import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";

interface ActiveRowState {
  activeRows: Record<string, string | null>;
}

const initialState: ActiveRowState = {
  activeRows: {},
};

const activeRowSlice = createSlice({
  name: "activeRow",
  initialState,
  reducers: {
    setActiveRow: (
      state,
      action: PayloadAction<{ tableName: string; rowId: string | null }>
    ) => {
      state.activeRows[action.payload.tableName] = action.payload.rowId;
    },
    clearActiveRow: (state, action: PayloadAction<string>) => {
      delete state.activeRows[action.payload];
    },
    clearAllActiveRows: (state) => {
      state.activeRows = {};
    },
  },
});

export const { setActiveRow, clearActiveRow, clearAllActiveRows } =
  activeRowSlice.actions;

export const selectActiveRow = (
  state: { activeRow: ActiveRowState },
  tableName: string
): string | null => {
  return state.activeRow.activeRows[tableName] ?? null;
};

export const selectAllActiveRows = (state: {
  activeRow: ActiveRowState;
}): Record<string, string | null> => {
  return state.activeRow.activeRows;
};

export default activeRowSlice.reducer;

// Hook for managing active row state for a specific table
export function useActiveRow(tableName: string) {
  const dispatch = useDispatch();
  const activeRowId = useSelector(
    (state: any) => selectActiveRow(state, tableName)
  );

  const setActive = useCallback((rowId: string | null) => {
    dispatch(setActiveRow({ tableName, rowId }));
  }, [dispatch, tableName]);

  const clear = useCallback(() => {
    dispatch(clearActiveRow(tableName));
  }, [dispatch, tableName]);

  return {
    activeRowId,
    setActive,
    clear,
  };
}

// Hook for accessing all active rows
export function useAllActiveRows() {
  const dispatch = useDispatch();
  const activeRows = useSelector(
    (state: any) => selectAllActiveRows(state)
  );

  const clearAll = useCallback(() => {
    dispatch(clearAllActiveRows());
  }, [dispatch]);

  return {
    activeRows,
    clearAll,
  };
}
